import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import RecitationAudioPlayer from "../components/RecitationAudioPlayer";
import MistakeHeatmap from "../components/MistakeHeatmap";
import VoiceRecorder from "../components/VoiceRecorder";
import { toast } from "sonner";
import { Inbox, CheckCircle2, Star, Send, Loader2 } from "lucide-react";

const RUBRIC_CRITERIA = [
  { key: "tajweed", label: "Tajweed" },
  { key: "memorization", label: "Memorization" },
  { key: "fluency", label: "Fluency" },
  { key: "makhraj", label: "Makhraj" },
];

export default function GradingInbox() {
  const [tab, setTab] = useState("pending");
  const [subs, setSubs] = useState(null);
  const [active, setActive] = useState(null);
  const [rubric, setRubric] = useState({ tajweed: 4, memorization: 4, fluency: 4, makhraj: 4 });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setSubs(null);
    api.get(`/teacher/submissions?status=${tab}`)
      .then((r) => {
        setSubs(r.data);
        if (!r.data.find((s) => s.submission_id === active?.submission_id)) setActive(null);
      })
      .catch(() => { toast.error("Couldn't load grading inbox"); setSubs([]); });
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [tab]);

  const avg = Math.round(
    (rubric.tajweed + rubric.memorization + rubric.fluency + rubric.makhraj) / 4,
  );

  const grade_now = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post("/teacher/grade", { submission_id: active.submission_id, note, ...rubric });
      toast.success("Graded · student notified");
      setActive(null); setNote(""); setRubric({ tajweed: 4, memorization: 4, fluency: 4, makhraj: 4 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't grade");
    } finally { setBusy(false); }
  };

  const sendVoice = async (blob, mime) => {
    if (!active) return;
    const fd = new FormData();
    fd.append("audio", blob, `voice.${(mime || "audio/webm").split("/")[1].split(";")[0]}`);
    const r = await fetch(`${API}/teacher/grade/${active.submission_id}/voice`, {
      method: "POST", body: fd, credentials: "include",
    });
    if (!r.ok) { toast.error("Voice upload failed"); throw new Error("fail"); }
    toast.success("Voice note attached");
    load();
  };

  return (
    <div className="space-y-6" data-testid="grading-inbox-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Grading inbox</div>
          <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Review your halaqa</h1>
          <p className="mt-2 text-foreground/70">Listen, see the AI mistake heatmap, and grade.</p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-sand-200 border border-sand-300" data-testid="grading-tabs">
          {["pending", "graded"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                tab === t ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {t === "pending" ? <Inbox className="h-3.5 w-3.5 inline mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 inline mr-1.5" />}
              {t === "pending" ? "Pending" : "Graded"}
            </button>
          ))}
        </div>
      </header>

      <div className="grid gap-5">
        {/* List */}
        <aside className="space-y-2 max-h-[80vh] overflow-y-auto pr-1">
          {subs === null && (
            <div className="hifz-card flex items-center gap-2 text-forest-700 text-sm" data-testid="inbox-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading inbox…
            </div>
          )}
          {subs && subs.length === 0 && (
            <div className="hifz-card text-sm text-muted-foreground text-center" data-testid="empty-inbox">
              {tab === "pending" ? "No pending submissions — caught up!" : "No graded items yet."}
            </div>
          )}
          {(subs || []).map((s) => (
            <button
              key={s.submission_id}
              data-testid={`sub-${s.submission_id}`}
              onClick={() => setActive(s)}
              className={`w-full text-left p-4 rounded-2xl border transition ${
                active?.submission_id === s.submission_id ? "border-forest-600 bg-forest-600/8" : "border-sand-300 bg-white hover:bg-sand-50"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-semibold text-forest-700">{s.student_name}</div>
                {s.status === "graded" ? (
                  <div className="flex" data-testid={`row-stars-${s.submission_id}`}>
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`h-3.5 w-3.5 ${n <= (s.teacher_grade||0) ? "text-gold-500 fill-gold-500" : "text-sand-300"}`} />
                    ))}
                  </div>
                ) : (
                  <span className="status-pill status-learning text-[10px]">{Math.round(s.ai_accuracy||0)}% AI</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Surah {s.surah} · Ayah {s.ayah}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{new Date(s.submitted_at).toLocaleString()}</div>
            </button>
          ))}
        </aside>

        {/* Detail */}
        <section>
          {!active ? (
            <div className="hifz-card text-center text-muted-foreground py-16" data-testid="no-active">
              <Inbox className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 text-sm">Pick a submission to review</p>
            </div>
          ) : (
            <div className="hifz-card space-y-6" data-testid="active-submission">
              <header className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{active.student_name} · {active.class_name}</div>
                  <h2 className="font-display font-semibold text-2xl text-forest-700">Surah {active.surah} · Ayah {active.ayah}</h2>
                  <div className="text-xs text-muted-foreground mt-1">Submitted {new Date(active.submitted_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {active.test_mode && active.test_mode !== "single" && (
                    <span
                      className="status-pill bg-terracotta-500/15 text-terracotta-700 border border-terracotta-500/40"
                      data-testid="test-mode-pill"
                    >
                      {active.test_mode === "live_asr" ? "Live practice" : "Self"} test
                    </span>
                  )}
                  <span className="status-pill status-memorized">AI Accuracy {active.ai_accuracy}%</span>
                  {active.ai_word_accuracy != null && (
                    <span className="status-pill status-learning">AI Words right {active.ai_word_accuracy}%</span>
                  )}
                </div>
              </header>

              {active.student_note && (
                <div className="rounded-xl bg-sand-200/50 border border-sand-300 p-3 text-sm italic text-foreground/75">
                  "{active.student_note}"
                </div>
              )}

              {active.mistakes_timeline?.length > 0 && (
                <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-4" data-testid="mistakes-timeline">
                  <div className="text-xs uppercase tracking-[0.18em] text-terracotta-700 font-semibold mb-2">
                    Live ASR mistake timeline · {active.mistakes_timeline.length} flagged
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {active.mistakes_timeline.map((m, i) => (
                      <li key={i} className="flex items-baseline gap-2 flex-wrap" data-testid={`mistake-${i}`}>
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          {Math.round((m.ts || 0) / 1000)}s
                        </span>
                        <span className="text-muted-foreground">expected</span>
                        <span className="ayah-arabic text-emerald-700" dir="rtl">{m.expected}</span>
                        <span className="text-muted-foreground">· heard</span>
                        <span className="ayah-arabic text-terracotta-600" dir="rtl">{m.said}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {active.attempt?.expected && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Expected ayah</div>
                  <div className="ayah-arabic text-3xl text-forest-700">{active.attempt.expected}</div>
                </div>
              )}

              {active.attempt?.has_audio && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Student recitation</div>
                  <RecitationAudioPlayer attemptId={active.attempt_id} testid="active-audio" />
                </div>
              )}

              {active.attempt?.word_diff?.length > 0 && (
                <MistakeHeatmap
                  wordDiff={active.attempt.word_diff}
                  extras={active.attempt.extras}
                  surah={active.surah}
                  ayah={active.ayah}
                />
              )}

              {active.status === "pending" ? (
                <div className="rounded-2xl border border-sand-300 bg-sand-200/40 p-5 space-y-4" data-testid="grade-form">
                  <div className="flex items-end justify-between gap-3 flex-wrap">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Grading rubric</div>
                    <div className="text-sm">Avg grade: <span className="font-display font-semibold text-forest-700 text-lg" data-testid="rubric-avg">{avg}</span> / 5</div>
                  </div>
                  <div className="grid gap-3" data-testid="rubric-grid">
                    {RUBRIC_CRITERIA.map((c) => (
                      <div key={c.key} className="rounded-xl border border-sand-300 bg-white p-3">
                        <div className="text-xs font-semibold text-foreground/80">{c.label}</div>
                        <div className="flex items-center gap-1 mt-1.5" data-testid={`rubric-${c.key}`}>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => setRubric({ ...rubric, [c.key]: n })} data-testid={`rubric-${c.key}-${n}`} className="p-0.5">
                              <Star className={`h-5 w-5 ${n <= rubric[c.key] ? "text-gold-500 fill-gold-500" : "text-sand-300"}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <textarea
                    data-testid="grade-note"
                    value={note} onChange={(e) => setNote(e.target.value)}
                    rows={3} placeholder="Notes for the student — focus on madd, makhraj, rhythm…"
                    className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm"
                  />
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <VoiceRecorder onRecorded={sendVoice} testidPrefix="grade-voice" />
                    <button onClick={grade_now} disabled={busy} className="btn-primary" data-testid="submit-grade-btn">
                      <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send grade"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-forest-600/30 bg-forest-600/8 p-5 space-y-3" data-testid="grade-display">
                  <div className="flex items-center gap-3">
                    <div className="flex">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} className={`h-5 w-5 ${n <= active.teacher_grade ? "text-gold-500 fill-gold-500" : "text-sand-300"}`} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">graded {new Date(active.graded_at).toLocaleDateString()}</span>
                  </div>
                  {active.teacher_rubric && (
                    <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="graded-rubric">
                      {RUBRIC_CRITERIA.map((c) => (
                        <span key={c.key} className="px-2 py-0.5 rounded-full bg-sand-100 border border-sand-300">
                          {c.label}: <strong>{active.teacher_rubric[c.key]}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                  {active.teacher_note && <p className="text-sm text-foreground/85 leading-relaxed">{active.teacher_note}</p>}
                  {active.voice_path && (
                    <audio
                      controls
                      src={`${API}/recitation/submissions/${active.submission_id}/voice`}
                      className="w-full mt-2"
                      data-testid="graded-voice"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
